import React from 'react'
import { motion } from 'framer-motion'
import { Image, Plus, Upload, Loader2, X, Send, Calendar, ChevronDown } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { getRoomDisplayName } from '../helpers'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface RoomPhotosSectionProps {
  isCustomer: VM['isCustomer']
  allPhotos: VM['allPhotos']
  selectedBladeFiles: VM['selectedBladeFiles']
  uploading: VM['uploading']
  dragOver: VM['dragOver']
  handleBladeFileSelect: VM['handleBladeFileSelect']
  removeSelectedBladeFile: VM['removeSelectedBladeFile']
  handleUploadSelectedBlade: VM['handleUploadSelectedBlade']
  handleDragOver: VM['handleDragOver']
  handleDragLeave: VM['handleDragLeave']
  handleDrop: VM['handleDrop']
  handleUploadClick: VM['handleUploadClick']
  openPhotoModal: VM['openPhotoModal']
  handleDeletePhotoFromList: VM['handleDeletePhotoFromList']
  selectedYear: VM['selectedYear']
  setSelectedYear: VM['setSelectedYear']
  yearOptions: VM['yearOptions']
  isYearDropdownOpen: VM['isYearDropdownOpen']
  setIsYearDropdownOpen: VM['setIsYearDropdownOpen']
  yearDropdownRef: VM['yearDropdownRef']
  bladeRoomSelection: VM['bladeRoomSelection']
  setBladeRoomSelection: VM['setBladeRoomSelection']
  isBladeRoomDropdownOpen: VM['isBladeRoomDropdownOpen']
  setIsBladeRoomDropdownOpen: VM['setIsBladeRoomDropdownOpen']
}

const RoomPhotosSection: React.FC<RoomPhotosSectionProps> = ({
  isCustomer,
  allPhotos,
  selectedBladeFiles,
  uploading,
  dragOver,
  handleBladeFileSelect,
  removeSelectedBladeFile,
  handleUploadSelectedBlade,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleUploadClick,
  openPhotoModal,
  handleDeletePhotoFromList,
  selectedYear,
  setSelectedYear,
  yearOptions,
  isYearDropdownOpen,
  setIsYearDropdownOpen,
  yearDropdownRef,
  bladeRoomSelection,
  setBladeRoomSelection,
  isBladeRoomDropdownOpen,
  setIsBladeRoomDropdownOpen,
}) => {
  const { t } = useLanguage()

  return (
    <>
          {/* Room Photos Section - Hidden for Customer */}
          {!isCustomer && (
          <div className="bg-gradient-to-br from-white via-blue-50/30 to-green-50/20 rounded-xl shadow-lg border border-blue-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-xl flex items-center justify-center shadow-md">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">{t.turbineDetail.photos.bladeSelectionAndPhotos}</h2>
                    <p className="text-sm text-gray-600">{t.turbineDetail.photos.bladeSelectionDescription}</p>
                  </div>
                </div>
                
                {/* Custom Year Dropdown */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {t.turbineDetail.photos.year}:
                  </label>
                  <div className="relative" ref={yearDropdownRef}>
                    <button
                      onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                      className="bg-white border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium text-left h-10 flex items-center justify-between min-w-[80px] hover:border-gray-400 transition-colors"
                    >
                      <span className="text-gray-900">{selectedYear}</span>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-2 ${isYearDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isYearDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                        {yearOptions.map(year => (
                          <button
                            key={year}
                            onClick={() => {
                              setSelectedYear(year)
                              setIsYearDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                              selectedYear === year ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  

                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              {Array.from({ length: 3 }, (_, index) => {
                const roomLetter = String.fromCharCode(65 + index) // A, B, C
                const roomNumber = bladeRoomSelection[index] || 1 // Use dynamic room selection for each blade
                const roomPhotos = allPhotos.filter(photo => 
                  photo.room === roomNumber.toString() && 
                  photo.blade && photo.blade !== null && photo.blade !== undefined && photo.blade !== '' &&
                  (!photo.category || photo.category === null || photo.category === undefined) &&
                  (!photo.blade || photo.blade === null || photo.blade === undefined)
                ) || []
                const bladeKey = `blade-${String.fromCharCode(65 + index)}` // blade-A, blade-B, blade-C
                const selectedFiles = selectedBladeFiles[bladeKey] || []  // Bu blade'e seçilen file'lar
                const hasSelectedFiles = selectedFiles.length > 0
                const isBladeUploading = uploading[bladeKey]  // Bu blade'in upload durumu
                const combinedPhotos = [...roomPhotos, ...selectedFiles]
                
                // Always show blade sections for photo upload
                // if (combinedPhotos.length === 0 && !isRoomUploading) {
                //   return null
                // }
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-gradient-to-r from-white to-blue-50/50 border border-blue-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                          <span className="text-blue-700 font-bold text-sm">{roomLetter}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Blade {roomLetter}
                          </h3>
                          {/* Room Selection Dropdown */}
                          <div className="relative">
                            <button
                              onClick={() => setIsBladeRoomDropdownOpen(prev => ({
                                ...prev,
                                [index]: !prev[index]
                              }))}
                              className="bg-white border-2 border-blue-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium text-left h-8 flex items-center justify-between min-w-[140px] hover:border-blue-400 transition-colors"
                            >
                              <span className="text-gray-900">{getRoomDisplayName(bladeRoomSelection[index] || 1)}</span>
                              <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ml-2 ${isBladeRoomDropdownOpen[index] ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {isBladeRoomDropdownOpen[index] && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-48 overflow-y-auto">
                                {Array.from({ length: 25 }, (_, roomIdx) => {
                                  const roomNumber = roomIdx + 1
                                  const photosInRoom = allPhotos.filter(photo =>
                                    photo.room === roomNumber.toString() &&
                                    (!photo.category || photo.category === null || photo.category === undefined)
                                  ).length
                                  const isEmpty = photosInRoom === 0

                                  return (
                                    <button
                                      key={roomNumber}
                                      onClick={() => {
                                        setBladeRoomSelection(prev => ({
                                          ...prev,
                                          [index]: roomNumber
                                        }))
                                        setIsBladeRoomDropdownOpen(prev => ({
                                          ...prev,
                                          [index]: false
                                        }))
                                      }}
                                      className={`w-full px-4 py-2 text-left hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                                        bladeRoomSelection[index] === roomNumber ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span>{getRoomDisplayName(roomNumber)}</span>
                                        {isEmpty ? (
                                          <span className="text-xs text-gray-400 ml-2">(Boş)</span>
                                        ) : (
                                          <span className="text-xs text-blue-600 ml-2">({photosInRoom} foto)</span>
                                        )}
                                      </div>
                                    </button>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        {hasSelectedFiles && !isBladeUploading && (
                          <button
                            onClick={() => handleUploadSelectedBlade(bladeKey, bladeRoomSelection[index] || 1)}  // Bu blade'e seçilen room'a yükle
                            className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm font-medium"
                          >
                            <Send className="w-4 h-4 mr-1.5" />
                            {selectedBladeFiles[bladeKey]?.length || 0} Fotoğraf Yükle
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Seçilen Dosyaların Önizlemesi */}
                    {hasSelectedFiles && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-blue-900">
                            📎 {t.turbineDetail.photos.selectedPhotos} ({selectedFiles.length})
                          </h4>
                          {isBladeUploading && (
                            <div className="flex items-center text-blue-600">
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              <span className="text-xs">Yükleniyor...</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Seçilen Fotoğrafların Önizleme Grid'i */}
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="relative group">
                              <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden">
                                <img 
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                                <button
                                  onClick={() => removeSelectedBladeFile(bladeKey, idx)}  // Bu blade'den kaldır
                                  className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white rounded-full p-1 absolute top-1 right-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                                <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-50 px-1 py-0.5 rounded text-center">
                                  {file.name}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Photo Grid - Scrollable Layout */}
                    <div className="max-h-96 overflow-y-auto">
                      <div className="grid grid-cols-8 gap-3 p-2">
                        {/* {t.turbineDetail.photos.addPhoto} Butonu - İlk Grid İtemi */}
                        <div 
                          className={`upload-zone w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 relative overflow-hidden ${
                              dragOver === index 
                                ? 'border-primary-500 bg-primary-50' 
                                : isBladeUploading
                                ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                                : hasSelectedFiles
                                ? 'border-green-300 bg-green-50'
                                : 'border-gray-300 hover:border-primary-400 hover:bg-primary-25'
                            }`}
                            onDragOver={(e) => !isBladeUploading && handleDragOver(e, index)}
                            onDragLeave={!isBladeUploading ? handleDragLeave : undefined}
                            onDrop={(e) => !isBladeUploading && handleDrop(e, index)}
                            style={{ pointerEvents: isBladeUploading ? 'none' : 'auto' }}
                          >
                            <div 
                              className="text-center p-1 relative z-10 w-full h-full flex flex-col items-center justify-center"
                              onClick={(e) => !isBladeUploading && handleUploadClick(e, index)}
                            >
                              {isBladeUploading ? (
                                <>
                                  <Loader2 className="w-6 h-6 text-blue-500 mb-1 animate-spin" />
                                  <p className="text-xs text-blue-600 font-medium">Yükleniyor</p>
                                </>
                              ) : dragOver === index ? (
                                <>
                                  <Upload className="w-6 h-6 text-primary-500 mb-1" />
                                  <p className="text-xs text-primary-600 font-medium">Bırak</p>
                                </>
                              ) : (
                                <>
                                  <div className="relative">
                                    <Image className="w-6 h-6 text-gray-400 mb-1" />
                                    <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5 shadow-sm border border-gray-100">
                                      <Plus className="w-2 h-2 text-primary-500" />
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-500 font-medium">{t.turbineDetail.photos.addPhoto}</p>
                                </>
                              )}
                            </div>
                            <input
                              key={`file-input-${index}`}
                              id={`file-input-${index}`}
                              type="file"
                              accept="image/*,.pcd"
                              multiple
                              onChange={(e) => {
                                e.stopPropagation()
                                const files = Array.from(e.target.files || []).filter(file => 
                                  file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.pcd')
                                )
                                if (files.length > 0) {
                                  handleBladeFileSelect(bladeKey, files)  // Bu blade'e ekle
                                }
                                // Reset the input
                                e.target.value = ''
                              }}
                              className="hidden"
                              onClick={(e) => e.stopPropagation()}
                              disabled={isBladeUploading}
                            />
                          </div>

                        {/* Tüm fotoğraflar */}
                        {combinedPhotos.map((photo, photoIndex) => (
                          <div 
                            key={photoIndex} 
                            className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden hover:opacity-75 transition-opacity group relative cursor-pointer"
                            onClick={() => openPhotoModal(roomNumber || 1, photoIndex)}
                          >
                            <img 
                              src={'url' in photo ? photo.url : URL.createObjectURL(photo)}
                              alt={'url' in photo ? (photo.originalName || `Photo ${photoIndex + 1}`) : photo.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                  <Plus className="w-4 h-4 text-gray-600" />
                                </div>
                              </div>
                            </div>
                            {/* Delete button for uploaded photos */}
                            {'url' in photo && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeletePhotoFromList(photo)
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white rounded-full p-1 absolute top-1 right-1"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
          )}
    </>
  )
}

export default RoomPhotosSection
